import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Search, RefreshCw, Send, Eye, X, Package } from 'lucide-react';

const ItemRequestResponses = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [responseDetails, setResponseDetails] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [availableGrns, setAvailableGrns] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // View dialog
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingResponse, setViewingResponse] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'RM_STORE']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [responsesRes, requestsRes, employeesRes, itemsRes] = await Promise.all([
        api.get('/item-request-responses'),
        api.get('/item-requests'),
        api.get('/employees'),
        api.get('/items')
      ]);
      setData(responsesRes.data);
      setRequests(requestsRes.data);
      setEmployees(employeesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.employee_code} - ${emp.first_name} ${emp.last_name || ''}` : `ID: ${employeeId}`;
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.code} - ${item.name}` : `ID: ${itemId}`;
  };

  const getRequestLabel = (requestId) => {
    const request = requests.find(r => r.id === requestId);
    return request ? `REQ-${request.request_no}` : `ID: ${requestId}`;
  };

  // Fetch available GRNs for an item
  const fetchAvailableGrns = async (itemId) => {
    if (!itemId || availableGrns[itemId]) return;
    try {
      const response = await api.get(`/items/${itemId}/available-grns`);
      setAvailableGrns(prev => ({ ...prev, [itemId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch GRNs for item', itemId);
    }
  };

  const handleOpenDialog = async (request = null) => {
    if (request) {
      // Issue items for a specific request
      setSelectedRequest(request);
      setFormData({
        item_request_id: request.id.toString(),
        response_date: new Date().toISOString().split('T')[0],
        employee_id: '',
        remarks: '',
      });
      
      // Initialize response details from pending items
      const pendingItems = (request.details || []).filter(d => {
        const requested = parseFloat(d.quantity) || 0;
        const issued = parseFloat(d.issued_quantity) || 0;
        return issued < requested;
      }) || [];
      
      setResponseDetails(pendingItems.map(d => ({
        item_id: d.item_id.toString(),
        quantity: '',
        purchase_id: '',
        pending: (parseFloat(d.quantity) || 0) - (parseFloat(d.issued_quantity) || 0),
      })));
      
      // Fetch available GRNs for each item
      for (const d of pendingItems) {
        fetchAvailableGrns(d.item_id);
      }
    } else {
      setSelectedRequest(null);
      setFormData({
        item_request_id: '',
        response_date: new Date().toISOString().split('T')[0],
        employee_id: '',
        remarks: '',
      });
      setResponseDetails([]);
    }
    setIsDialogOpen(true);
  };

  const handleRequestChange = async (requestId) => {
    const request = requests.find(r => r.id === parseInt(requestId));
    setSelectedRequest(request);
    setFormData(prev => ({ ...prev, item_request_id: requestId }));
    
    if (request) {
      // Initialize response details from pending items
      const pendingItems = (request.details || []).filter(d => {
        const requested = parseFloat(d.quantity) || 0;
        const issued = parseFloat(d.issued_quantity) || 0;
        return issued < requested;
      }) || [];
      
      setResponseDetails(pendingItems.map(d => ({
        item_id: d.item_id.toString(),
        quantity: '',
        purchase_id: '',
        pending: (parseFloat(d.quantity) || 0) - (parseFloat(d.issued_quantity) || 0),
      })));
      
      // Fetch available GRNs for each item
      for (const d of pendingItems) {
        await fetchAvailableGrns(d.item_id);
      }
    }
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...responseDetails];
    updated[index] = { ...updated[index], [field]: value };
    setResponseDetails(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.item_request_id || !formData.employee_id || !formData.response_date) {
      toast.error('Request, Employee and Date are required');
      return;
    }
    
    const validDetails = responseDetails.filter(d => d.quantity && parseFloat(d.quantity) > 0);
    if (validDetails.length === 0) {
      toast.error('At least one item with quantity is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        item_request_id: parseInt(formData.item_request_id),
        response_date: formData.response_date,
        employee_id: parseInt(formData.employee_id),
        remarks: formData.remarks || null,
        details: validDetails.map(d => ({
          item_id: parseInt(d.item_id),
          quantity: parseFloat(d.quantity),
          purchase_id: d.purchase_id ? parseInt(d.purchase_id) : null,
        })),
      };

      await api.post('/item-request-responses', submitData);
      toast.success('Items issued successfully');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this response? This will reverse the issued quantities.')) return;
    try {
      await api.delete(`/item-request-responses/${item.id}`);
      toast.success('Response deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const response = await api.get(`/item-request-responses/${item.id}`);
      setViewingResponse(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load response details');
    }
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const reqLabel = getRequestLabel(item.item_request_id).toLowerCase();
    const empName = getEmployeeName(item.employee_id).toLowerCase();
    return reqLabel.includes(term) || empName.includes(term);
  });

  // Filter requests to show only pending ones
  const pendingRequests = requests.filter(r => r.status === 'PENDING' || r.status === 'PARTIALLY_ISSUED');

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="item-request-responses">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Send className="w-6 h-6 text-sky-500" />
            Issue Items (Response)
          </h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Issue Items
          </Button>
        )}
      </div>

      {/* Pending Requests Quick Actions */}
      {pendingRequests.length > 0 && (
        <Card className="mb-4 border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-orange-800 mb-2">Pending Requests ({pendingRequests.length})</h3>
            <div className="flex flex-wrap gap-2">
              {pendingRequests.slice(0, 5).map(req => (
                <Button
                  key={req.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenDialog(req)}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  <Package className="w-4 h-4 mr-1" />
                  REQ-{req.request_no}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by Request No, Employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No responses found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Request</TableHead>
                    <TableHead>Response Date</TableHead>
                    <TableHead>Issued By</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">{getRequestLabel(item.item_request_id)}</TableCell>
                      <TableCell>{new Date(item.response_date).toLocaleDateString()}</TableCell>
                      <TableCell>{getEmployeeName(item.employee_id)}</TableCell>
                      <TableCell>{(item.details ? item.details.length : 0) || 0} items</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Issue Items Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Issue Items {selectedRequest && `- REQ-${selectedRequest.request_no}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
              <div>
                <Label className="form-label">Request <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.item_request_id}
                  onValueChange={handleRequestChange}
                  disabled={!!selectedRequest}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Request" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {pendingRequests.map((req) => (
                      <SelectItem key={req.id} value={req.id.toString()}>
                        REQ-{req.request_no} ({req.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.response_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, response_date: e.target.value }))}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Issued By <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, employee_id: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Employee" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {employees.filter(e => e.is_active).map((emp) => (
                      <SelectItem key={emp.id} value={emp.id.toString()}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Items to Issue */}
            <div className="mt-4 border-t pt-4">
              <h3 className="font-semibold text-slate-900 mb-3">Items to Issue</h3>
              
              {responseDetails.length === 0 ? (
                <div className="text-center py-6 text-slate-500 border border-dashed rounded-sm">
                  Select a request to see items to issue
                </div>
              ) : (
                <div className="border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Item</TableHead>
                        <TableHead>Pending Qty</TableHead>
                        <TableHead>Issue Qty</TableHead>
                        <TableHead>From GRN</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {responseDetails.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{getItemName(parseInt(detail.item_id))}</TableCell>
                          <TableCell className="font-mono text-orange-600">{(detail.pending || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.0001"
                              max={detail.pending}
                              value={detail.quantity}
                              onChange={(e) => handleDetailChange(index, 'quantity', e.target.value)}
                              placeholder="0"
                              className="rounded-sm w-28"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={detail.purchase_id || "none"}
                              onValueChange={(val) => handleDetailChange(index, 'purchase_id', val === "none" ? "" : val)}
                            >
                              <SelectTrigger className="rounded-sm w-40">
                                <SelectValue placeholder="Select GRN" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No GRN</SelectItem>
                                {(availableGrns[parseInt(detail.item_id)] || []).map((grn) => (
                                  <SelectItem key={grn.purchase_id} value={grn.purchase_id.toString()}>
                                    GRN-{grn.grn_no} (Qty: {grn.quantity})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <div className="mt-4">
              <Label className="form-label">Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Any additional notes..."
                className="rounded-sm"
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Issuing...' : 'Issue Items'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Response Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Issue Details</DialogTitle>
          </DialogHeader>
          {viewingResponse && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-slate-500">Request</span>
                  <p className="font-medium">{getRequestLabel(viewingResponse.item_request_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Date</span>
                  <p className="font-medium">{new Date(viewingResponse.response_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Issued By</span>
                  <p className="font-medium">{getEmployeeName(viewingResponse.employee_id)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Issued Items ({(viewingResponse.details ? viewingResponse.details.length : 0) || 0})</h4>
                {(viewingResponse.details ? viewingResponse.details.length : 0) > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Item</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>From GRN</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingResponse.details.map((detail, index) => {
                        const purchase = requests.find(r => r.id === viewingResponse.item_request_id);
                        return (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{getItemName(detail.item_id)}</TableCell>
                            <TableCell className="font-mono">{parseFloat(detail.quantity).toFixed(2)}</TableCell>
                            <TableCell>{detail.purchase_id ? `GRN-${detail.purchase_id}` : '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-slate-500">No items issued</p>
                )}
              </div>

              {viewingResponse.remarks && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Remarks</h4>
                  <p className="text-slate-600">{viewingResponse.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemRequestResponses;
